/**
 * web/src/screens/Notebook/hooks/feed.ts — the feed + list seam of
 * useNotebookState (card vncVrGk1hc9i, round two): the item list, tags and
 * suggestions, the reload machinery with its newest-response guard, and the
 * row actions (done toggle, quiet remove with undo, the remove permission).
 * Pure move out of the former hooks.ts — no behaviour change.
 */
import { useCallback, useRef, useState } from 'react';
import { api, type Session } from '../../../shell/bridge';
import { ENTRY_LABEL, type NotebookItem, type NotebookTag, type Suggestion } from '../types';

export function useNotebookFeed({ me }: { me: Session | null }) {
  const [items, setItems] = useState<NotebookItem[] | null>(null);
  const [failed, setFailed] = useState(false);

  const [busyId, setBusyId] = useState<string | null>(null);

  // Mood / theme / time tags and whatever the last scan left waiting (card
  // ioKcDXGeHnE7) — same fetch cadence as the cards themselves.
  const [tags, setTags] = useState<NotebookTag[]>([]);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);

  const [rowError, setRowError] = useState<{ itemId: string; message: string } | null>(null);
  const rowErrorTimer = useRef<number | undefined>(undefined);

  /* Card xUZFWf8JtHo2 item 5 — quiet remove. Removing a card no longer opens a
     confirm modal; it acts immediately and leaves a "Removed — Undo" line for a
     few seconds. Removal is a hard DELETE server-side, so Undo re-creates the
     row from the fields kept here (POST /api/notebook) rather than flipping a
     soft-delete flag. Undo is the safety net a modal used to be, without the
     interruption. */
  const [removedItem, setRemovedItem] = useState<{ item: NotebookItem; label: string } | null>(null);
  const undoTimer = useRef<number | undefined>(undefined);
  /* Newest-response guard for reload(): the unfurl polls and a mutation's own
   * refetch all call it, and an older answer landing after a newer one used to
   * resurrect a row that had just been removed. Only the latest call applies. */
  const reloadSeq = useRef(0);
  /** Kept current so a callback defined before `reload` (the capture toast's
   *  Undo) can refetch without depending on the render order. */
  const reloadRef = useRef<(() => Promise<void>) | null>(null);
  /** The collection search's current query (card UVRx4xesTEig). The search is
   *  a SERVER query (GET /api/notebook?q=…), not a client-side filter — the
   *  list is paginated, so a page the client never received cannot be
   *  filtered into existence, and a tag match has to happen in the DO's
   *  WHERE. Kept in a ref so reload() always fetches with the live query. */
  const searchRef = useRef('');


  /* Items and tags are independent loads (sol review 2026-10-19, card
     xNGOA5rZz9LV phase 3, high): they used to ride one Promise.all, so a tag
     endpoint outage blanked a perfectly loaded notebook and the catch set the
     whole panel to its failed state. Each half now answers for itself — the
     list is the product, tags are decoration on top of it — and the
     newest-response guard keeps an older answer from overwriting a newer one
     (that is the stale-overlap path that resurrected a just-removed row). */
  const reload = useCallback(async () => {
    const seq = ++reloadSeq.current;
    const q = searchRef.current.trim();
    const [itemsRes, tagsRes] = await Promise.allSettled([
      api<{ items: NotebookItem[] }>(q ? `/api/notebook?q=${encodeURIComponent(q)}` : '/api/notebook'),
      api<{ tags: NotebookTag[]; suggestions: Suggestion[] }>('/api/notebook/tags'),
    ]);
    if (seq !== reloadSeq.current) return; // a newer reload already answered
    if (itemsRes.status === 'fulfilled') {
      setItems(itemsRes.value.items ?? []);
      setFailed(false);
    } else {
      console.error('[Pear] notebook load failed', itemsRes.reason);
      setItems([]);
      setFailed(true);
    }
    if (tagsRes.status === 'fulfilled') {
      setTags(tagsRes.value.tags ?? []);
      setSuggestions(tagsRes.value.suggestions ?? []);
    } else {
      // Keep whatever tags are already on screen: the cards render fine
      // without a fresh tag list, they just do not gain new ones.
      console.error('[Pear] notebook tags load failed', tagsRes.reason);
    }
  }, []);

  /** A per-card failure, said on the card that failed and gone after 6s —
   *  long enough to read, short enough not to become furniture. */
  const flashRowError = useCallback((itemId: string, message: string) => {
    setRowError({ itemId, message });
    window.clearTimeout(rowErrorTimer.current);
    rowErrorTimer.current = window.setTimeout(() => setRowError(null), 6000);
  }, []);

  const toggleDone = async (item: NotebookItem) => {
    if (busyId) return;
    setBusyId(item.itemId);
    try {
      /* One canonical lifecycle field (sol review 2026-10-19, card
         xNGOA5rZz9LV): `status` is the facet the filter above reads, and the
         Durable Object keeps it and doneAt in step (markNotebookDone writes
         both in one UPDATE). Toggling off `status` means the verb on the card
         and the Done filter can never disagree about the same row. */
      await api(`/api/notebook/${item.itemId}`, { method: 'PUT', body: { done: item.status !== 'done' } });
      setRowError(null);
      await reload();
    } catch (e) {
      console.error('[Pear] done toggle failed', e);
      // The card is still showing the OLD state, so the only lie would be
      // silence. Say it on the row that failed (card xNGOA5rZz9LV phase 3).
      flashRowError(item.itemId, 'Couldn\'t update that card — nothing changed. Try again.');
    } finally {
      setBusyId(null);
    }
  };

  /** Notebook v2 typed entries are user-authored text — removing one should be
   *  possible (the card lives by choice, not by ingest). Deletes any entry.
   *
   *  Card xUZFWf8JtHo2 item 5 removed the confirm modal in favour of an
   *  immediate delete plus an 8-second "Removed — Undo" line.
   *
   *  Card snBw8Ua7Wnmc (Jonathan, email 2026-11-03) puts the confirmation back:
   *  the undo window was not enough — he removed a memory by accident and asked
   *  for it restored (card 4gQFLXE7xYIw). The row's Remove now only ASKS
   *  (`requestRemove`); `confirmRemove` is what issues the PUT. Undo stays as
   *  the second net, so a confirmed delete is still reversible for a few
   *  seconds. */
  const performRemove = async (item: NotebookItem) => {
    if (busyId) return;
    setBusyId(item.itemId);
    try {
      await api(`/api/notebook/${item.itemId}`, { method: 'PUT', body: { removed: true } });
      const label = item.entryType === 'link'
        ? (item.title?.trim() || item.url)
        : (item.body?.trim() || ENTRY_LABEL[item.entryType]);
      setRemovedItem({ item, label });
      window.clearTimeout(undoTimer.current);
      undoTimer.current = window.setTimeout(() => setRemovedItem(null), 8000);
      setRowError(null);
      await reload();
    } catch (e) {
      console.error('[Pear] notebook remove failed', e);
      // The card is still on screen (the PUT failed), so say so on the row.
      flashRowError(item.itemId, "Couldn't remove that — the card is still here. Try again.");
    } finally {
      setBusyId(null);
    }
  };

  /* Card snBw8Ua7Wnmc — the ask. Every Remove in the Collection (typed card,
     link card, detail panel) calls this; the sheet it opens is shared. */
  const [pendingRemove, setPendingRemove] = useState<NotebookItem | null>(null);
  const requestRemove = (item: NotebookItem) => {
    if (busyId) return;
    setPendingRemove(item);
  };
  const cancelRemove = () => setPendingRemove(null);
  const confirmRemove = async () => {
    const item = pendingRemove;
    if (!item) return;
    /* Close the sheet BEFORE the PUT so the row's own busy state is what the
       user sees next; a failed delete re-surfaces as the row error. */
    setPendingRemove(null);
    await performRemove(item);
  };

  /* Card xUZFWf8JtHo2 item 5 — the other half of the quiet remove. Removal is a
     hard DELETE, so Undo re-creates the row from the fields captured at remove
     time: a typed entry re-posts its body + visibility, a link re-posts its URL.
     A re-created row gets a fresh itemId (and a re-unfurl for a link), which is
     the honest cost of a delete that is a real delete — the card comes back with
     its content, not its identity. */
  const undoRemove = async () => {
    if (!removedItem || busyId) return;
    const { item } = removedItem;
    setBusyId(item.itemId);
    try {
      const payload = item.entryType === 'link'
        ? { url: item.url, shared: item.shared }
        : { entryType: item.entryType, body: item.body ?? '', shared: item.shared };
      await api('/api/notebook', { body: payload });
      setRemovedItem(null);
      window.clearTimeout(undoTimer.current);
      setRowError(null);
      await reload();
    } catch (e) {
      console.error('[Pear] notebook undo failed', e);
      flashRowError(item.itemId, "Couldn't bring that card back — try removing it again to refresh.");
    } finally {
      setBusyId(null);
    }
  };

  /* Card Q4BluQX8fXgj — only the creator can remove an entry, even a shared
     "us" one (Jonathan, 2026-10-16). The server enforces it; this hides the
     control so a partner never sees a Remove button they can't use. */
  const canRemove = (item: NotebookItem) => me !== null && item.addedBy === me.memberId;

  return {
    items, setItems, failed, setFailed, busyId, setBusyId,
    tags, setTags, suggestions, setSuggestions,
    rowError, setRowError, rowErrorTimer,
    removedItem, setRemovedItem, undoTimer,
    reloadSeq, reloadRef, searchRef, reload,
    /* Card snBw8Ua7Wnmc — `deleteEntry` is what every Remove control calls and
       it only ASKS; `confirmRemove` performs. Keeping the old export name means
       the cards, the detail panel and their call sites stay untouched. */
    flashRowError, toggleDone, deleteEntry: requestRemove, canRemove,
    undoRemove, pendingRemove, cancelRemove, confirmRemove,
  };
}

export type NotebookFeed = ReturnType<typeof useNotebookFeed>;
